import { useState, useEffect } from "react";
import BaseModal from "../../BaseComponents/BaseModal/BaseModal";
import SelectBox from "../../BaseComponents/SelectBox/SelectBox";
import ToggleSwitch from "../../BaseComponents/Toggle/ToggleSwitch";
import MarketingTextSection from "../MarketingText/MarketingText";
import { useToast } from "../../../context/ToastContext";
import styles from "./AddEditHolidayTypeModal.module.css";

const emptyForm = {
  name: "",
  category: "Holiday",
  icon: "☀️",
  active: true,
  sortOrder: 0,
  marketing: {
    title: "",
    para1: "",
    para2: "",
  },
};

const toForm = (data) => {
  if (!data) return emptyForm;
  return {
    name: data.name || "",
    category: data.category === "Theme" ? "Theme" : "Holiday",
    icon: data.icon || "☀️",
    active: data.active ?? true,
    sortOrder: typeof data.sortOrder === "number" ? data.sortOrder : 0,
    marketing: {
      title: data.title ?? data.marketing?.title ?? "",
      para1: data.paragraph1 ?? data.marketing?.para1 ?? "",
      para2: data.paragraph2 ?? data.marketing?.para2 ?? "",
    },
  };
};

const AddEditHolidayTypeModal = ({
  open,
  onClose,
  mode = "add",
  initialData,
  onSave,
  saveLoading = false,
}) => {
  const { showToast } = useToast();
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && initialData) {
      setForm(toForm(initialData));
    } else {
      setForm(emptyForm);
    }
  }, [open, mode, initialData]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleMarketingChange = (marketing) => {
    setForm((prev) => ({ ...prev, marketing }));
  };

  const handleSubmit = () => {
    if (!form.name.trim()) {
      showToast("Name is required", "error");
      return;
    }

    const payload = {
      name: form.name.trim(),
      category: form.category,
      icon: form.icon.trim() || "☀️",
      active: form.active,
      sortOrder: Number(form.sortOrder) || 0,
      title: form.marketing.title.trim() || undefined,
      paragraph1: form.marketing.para1.trim() || undefined,
      paragraph2: form.marketing.para2.trim() || undefined,
    };

    // Pass editingId so parent knows create vs update (avoids stale state)
    const editingId = mode === "edit" && initialData?.id != null ? initialData.id : null;
    if (onSave) onSave(payload, editingId);
    else onClose();
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={mode === "edit" ? "Edit Holiday/Theme Type" : "Add Holiday/Theme Type"}
      width="700px"
      footer={
        <>
          <button
            className={styles.cancelBtn}
            onClick={onClose}
            disabled={saveLoading}
          >
            Cancel
          </button>
          <button
            className={`${styles.primaryBtn} ${saveLoading ? styles.primaryBtnLoading : ""}`}
            onClick={handleSubmit}
            disabled={saveLoading}
          >
            {saveLoading ? (
              <>
                <span className={styles.spinner} aria-hidden />
                Saving…
              </>
            ) : mode === "edit" ? (
              "Save Changes"
            ) : (
              "+ Create Type"
            )}
          </button>
        </>
      }
    >
      <div className={styles.formGrid}>
        <div className={styles.field}>
          <label>Name</label>
          <input
            placeholder="e.g. Sun Holidays"
            value={form.name}
            onChange={(e) => handleChange("name", e.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label>Category</label>
          <SelectBox
            placeholder="Select category"
            value={form.category}
            options={[
              { label: "Holiday", value: "Holiday" },
              { label: "Theme", value: "Theme" },
            ]}
            onChange={(e) => handleChange("category", e.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label>Icon (emoji)</label>
          <input
            placeholder="e.g. ☀️"
            value={form.icon}
            onChange={(e) => handleChange("icon", e.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label>Sort order</label>
          <input
            type="number"
            min={0}
            value={form.sortOrder}
            onChange={(e) => handleChange("sortOrder", e.target.value)}
          />
        </div>

        <div className={styles.toggleRow}>
          <div className={styles.field}>
            <label>Active</label>
            <ToggleSwitch
              checked={form.active}
              onChange={() => handleChange("active", !form.active)}
            />
          </div>
        </div>
      </div>

      <MarketingTextSection value={form.marketing} onChange={handleMarketingChange} />
    </BaseModal>
  );
};

export default AddEditHolidayTypeModal;
